import React, { useEffect, useState } from "react";
import { Form, Spinner, Button, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setInProgressProject } from "./redux/actions"; // Assuming you have an action for creating a project
import useDebounce from "../../../hooks/useDebounce";
import "./createProjectModal.css";
import { getAllUsers } from "../../../Datahandler";
import extractNamesFromEmail from "../../../utils/extractNameFromEmail";
import Members from "../../Shared/Members";
import createSummary from "../../../utils/openAI";

const CreateProjectModal = () => {
  const inProgressProject = useSelector(
    (state) => state.createProject.inProgressProject
  );
  const currentUser = useSelector((state) => state.user.loggedInUser);
  const dispatch = useDispatch();

  // Define project state
  const [project, setProject] = useState(
    inProgressProject || {
      title: "",
      description: "",
      videoUrl: "",
      gitUrl: "",
      level: 1,
      members: [],
      status: "ongoing",
      imgUrl: "",
    }
  );

  // Use debounced values
  const debouncedDescription = useDebounce(project.description, 500);
  const debouncedTitle = useDebounce(project.title, 500);
  const debouncedVideoLink = useDebounce(project.videoUrl, 500);
  const debouncedGitLink = useDebounce(project.gitUrl, 500);

  const [isGenerating, setIsGenerating] = useState(false);
  const [allMembers, setAllMembers] = useState([]);

  useEffect(() => {
    getAllUsers().then((res) => {
      setAllMembers(res);
    });
  }, []);

  const handleAiGeneration = async () => {
    setIsGenerating(true);
    // Assuming you have a function for creating a project summary
    const response = await createSummary(project.title);
    setIsGenerating(false);
    setProject({ ...project, description: response });
  };

  const handleAddMember = (member) => {
    setProject({ ...project, members: [...project.members, member] });
  };

  const removeMember = (user) => {
    setProject({
      ...project,
      members: project.members.filter((member) => member !== user),
    });
  };

  useEffect(() => {
    if (
      debouncedDescription ||
      debouncedVideoLink ||
      debouncedTitle ||
      project.level ||
      debouncedGitLink ||
      project.members.length > 0 ||
      project.status
    ) {
      dispatch(
        setInProgressProject({
          ...project,
          projectLevel: parseInt(project.level),
        })
      );
    }
  }, [
    debouncedDescription,
    debouncedVideoLink,
    debouncedGitLink,
    debouncedTitle,
    project.level,
    project.members,
    project.status,
    dispatch,
    project,
  ]);

  return (
    <Form>
      <Form.Group className={"modalCreateProjectForm"}>
        <Form.Label>Project name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Title of your idea"
          value={project.title}
          onChange={(e) => setProject({ ...project, title: e.target.value })}
        />
        <Form.Label>Video Link</Form.Label>
        <Form.Control
          type="text"
          placeholder="Link to a video"
          value={project.videoUrl}
          onChange={(e) => setProject({ ...project, videoUrl: e.target.value })}
        />
        <Form.Label>Git Link</Form.Label>
        <Form.Control
          type="text"
          placeholder="Link to a git repository"
          value={project.gitUrl}
          onChange={(e) => setProject({ ...project, gitUrl: e.target.value })}
        />
        <Form.Label>Img Link</Form.Label>
        <Form.Control
          type="text"
          placeholder="Link to an image"
          value={project.imgUrl}
          onChange={(e) => setProject({ ...project, imgUrl: e.target.value })}
        />
        <Form.Label>Level of Visibility</Form.Label>
        <Form.Select
          aria-label="Level of Visibility"
          value={project.level}
          onChange={(e) => setProject({ ...project, level: e.target.value })}
        >
          <option>Level of Visibility</option>
          <option value={0} selected={project.level === 1}>
            Guest
          </option>
          {currentUser && currentUser.level >= 100 && (
            <option value={100}>Authorized Users</option>
          )}
          {currentUser && currentUser.level >= 1000 && (
            <option value={1000}>AIE / Admin</option>
          )}
        </Form.Select>
        <Form.Label>Status</Form.Label>
        <Form.Select
          aria-label="Status"
          value={project.status}
          onChange={(e) => setProject({ ...project, status: e.target.value })}
        >
          <option value={"ongoing"} selected={project.status === "ongoing"}>
            Ongoing
          </option>
          <option value={"completed"} selected={project.status === "completed"}>
            Completed
          </option>
        </Form.Select>
        <Form.Label>Members</Form.Label>
        <Form.Select
          aria-label="Members"
          onChange={(e) => handleAddMember(e.target.value)}
        >
          <option disabled selected={project.members.length === 0}>
            {project.members.join(", ")}
          </option>
          {allMembers.map((user) => {
            const name = extractNamesFromEmail(user.email);
            return (
              <option value={user.userId}>
                {name && name.length >= 2 ? name : user.email}
              </option>
            );
          })}
        </Form.Select>
        <h2>Members</h2>
        <h4>Click on a member to remove them.</h4>
        <Row>
          <Members handleClick={removeMember} memberIds={project.members} />
        </Row>
        <Form.Label>Project Description</Form.Label>
        <Form.Control
          as="textarea"
          placeholder="What's your dream?"
          value={project.description}
          maxLength={500}
          rows={7}
          onChange={(e) =>
            setProject({ ...project, description: e.target.value })
          }
        />
        <Form.Text className="text-muted">Maximum 500 Characters.</Form.Text>
        <section className={"aiSection"}>
          <section>
            <p>
              Need help with coming up with a description? Use the AI generator!
            </p>
            <Form.Text className="text-muted">
              Write a title then press the button, and the magic will happen!
              Please edit all the fields before sending!
            </Form.Text>
          </section>
          <Button
            variant="outline-primary"
            disabled={project.title?.length < 3}
            onClick={handleAiGeneration}
          >
            {isGenerating ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Generating....</span>
              </Spinner>
            ) : (
              "Create with AI!"
            )}
          </Button>
        </section>
      </Form.Group>
    </Form>
  );
};

export default CreateProjectModal;
